import React, { useCallback, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Progress } from '../Progress/Progress'
import { FavoriteButton } from '../FavoriteButton/FavoriteButton'
import useResizeObserver from '../../features/useResizeObserver'
import { apply } from '../../features/ContainerQuery'
import deprecated from '../../utils/deprecated'

/**
 * Bronson ProductCard component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-product-card.html
 *
 * @param {Array<Button>} [buttons] - A combination of a primary and secondary button to display main actions.
 * @param {Carousel} [carousel] - A collection of images that describe the product.
 * @param {boolean} [defaultExpanded] - Determines, if the ProductCard should be expanded by default.
 * @param {boolean} [disabled] - Option to disable the whole ProductCard.
 * @param {boolean} [empty] - Option to display an empty ProductCard.
 * @param {boolean} [expandable] - Optional expandable content to display further information.
 * @param {DescriptionList} [expandableContent] - Expandable content, that provides further information about product.
 * @param {DefinitionList} [expandableDefinitionList] - Deprecated, use {@link expandableContent} instead.
 * @param {string} [expandableText] - Text of expandable content.
 * @param {string} [expandableTitle] - Title of expandable content.
 * @param {string} [expandableTriggerLabel] - Label beside the icon, that triggers expandable content.
 * @param {boolean} [favoriteButton=true] - Option to render the {@link FavoriteButton} in the header of the ProductCard.
 * @param {Footnote} [footnotes] - Option to display footnotes at the bottom of the ProductCard.
 * @param {function} [onClick] - Defines `onClick()` event.
 * @param {PriceBox} [priceBox] - Display price of product.
 * @param {string} [productSubtitle] - Optional subtitle of product.
 * @param {string} [productTitle] - Title of product.
 * @param {string} [progressLabel] - Label of progress bar.
 * @param {StatusMessage} [status] - Option to display a status for the ProductCard.
 * @param {Tag} [tag] - Can be used to highlight, if the product is new or the price is reduced.
 * @param {TagList} [tagList] - Can be used to display search categories for product.
 * @param {string} [tagListTitle] - Title of tag list.
 * @param {string} [text] - Present information about the product inside the body.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The ProductCard element.
 * @constructor
 */

export function ProductCard({
  buttons,
  carousel,
  defaultExpanded,
  disabled,
  empty,
  expandable,
  expandableContent,
  expandableDefinitionList,
  expandableText,
  expandableTitle,
  expandableTriggerLabel,
  favoriteButton = true,
  footnotes,
  onClick,
  priceBox,
  productSubtitle,
  productTitle,
  progressLabel,
  status,
  tag,
  tagList,
  tagListTitle,
  text,
  testId,
  ...otherProps
}) {
  const cardRef = useRef(null)
  const [expanded, setExpanded] = useState(defaultExpanded)

  const applyQC = useCallback(apply, [apply])
  useResizeObserver({
    ref: cardRef,
    onResizeHandler: applyQC,
  })

  const classNameList = classNames('c-product-card', {
    'c-product-card--empty': empty, // Convenience prop from Bronson variants.
    'c-product-card--inactive': disabled, // Convenience prop from Bronson variants.
  }).trim()

  function renderIfFavoriteButton() {
    if (favoriteButton) {
      return (
        <div className="c-product-card__features__item c-product-card__features__item--fav-button">
          <FavoriteButton labelOff="Save for later" labelOn="Saved" className="c-fav-button--reversed" />
        </div>
      )
    }
    return null
  }

  function renderIfTag() {
    if (tag) {
      return <div className="c-product-card__features__item c-product-card__features__item--tag">{tag}</div>
    }
    return null
  }

  function renderIfSubtitle() {
    if (productSubtitle) {
      return <h5 className="c-product-card__subtitle">{productSubtitle}</h5>
    }
    return null
  }

  function renderIfProgress() {
    if (progressLabel) {
      return (
        <div className="c-product-card__progress__wrapper">
          <Progress value="20" className="c-product-card__progress" />
          <p className="c-product-card__progress__label">{progressLabel}</p>
        </div>
      )
    }
    return null
  }

  function renderIfBody() {
    if (text) {
      return (
        <div className="c-product-card__body">
          <p>{text}</p>
        </div>
      )
    }
    return null
  }

  function renderIfTitle() {
    if (expandableTitle) {
      return <h5 className="c-product-card__expandable-panel__title">{expandableTitle}</h5>
    }
    return null
  }

  function renderIfText() {
    if (expandableText) {
      return <p className="c-product-card__expandable-panel__text">{expandableText}</p>
    }
    return null
  }

  function renderIfExpandable() {
    if (expandable) {
      const expandableClassNameList = classNames('c-product-card__expandable', 'js-card-expandable', {
        'is-open': expanded,
      }).trim()

      return (
        <div className={expandableClassNameList}>
          <button
            type="button"
            className="c-btn c-btn--link c-btn--small c-btn--simple js-card-expandable__trigger"
            aria-expanded={expanded}
            onClick={() => {
              return setExpanded((prevExpanded) => !prevExpanded)
            }}
          >
            <i className="c-icon c-btn__icon c-icon--[semantic-expand]" aria-hidden="true" role="img" />
            <span className="c-btn__text">{expandableTriggerLabel}</span>
          </button>
          <div className="c-product-card__expandable-panel js-card-expandable__panel" aria-hidden={!expanded}>
            {renderIfTitle()}
            {renderIfText()}
            {expandableContent ?? expandableDefinitionList}
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <a className="c-btn c-btn--link c-btn--small" onClick={onClick}>
              <i className="c-btn__icon c-icon c-icon--[semantic-forward]" aria-hidden="true" role="img" />
              <span className="c-btn__text">See full details</span>
            </a>
          </div>
        </div>
      )
    }
    return null
  }

  function renderIfFootnotes() {
    if (footnotes) {
      return footnotes
    }
    return null
  }

  /**
   * Conditionally render a status message.
   * @returns {JSX.Element|null}
   */
  function renderIfStatus() {
    if (status) {
      return <div className="c-product-card__status">{status}</div>
    }
    return null
  }

  function renderFallbackImage() {
    return (
      <div className="c-product-card__media-fallback o-ratio o-ratio--16:9" aria-hidden="true">
        <i className="c-product-card__media-fallback__icon  c-icon  c-icon--[semantic-car]  o-ratio__content" />
      </div>
    )
  }

  return (
    <article
      ref={cardRef}
      className={classNameList}
      data-container-query="product-card"
      data-testid={testId}
      {...otherProps}
    >
      {!empty && (
        <div className="c-product-card__header">
          <div className="c-product-card__features">
            {renderIfFavoriteButton()}
            {renderIfTag()}
          </div>
          <div className="c-product-card__tag-list">
            <div className="c-product-card__tag-list__title">{tagListTitle}</div>
            {tagList}
          </div>
        </div>
      )}

      {!empty && (
        <div className="c-product-card__inline c-product-card__meta">
          <h4 className="c-product-card__title">{productTitle}</h4>
          {renderIfSubtitle()}
        </div>
      )}

      <div className="c-product-card__inline c-product-card__media">
        {/* eslint-disable-next-line no-nested-ternary */}
        {empty ? renderFallbackImage() : disabled ? carousel : carousel}
      </div>

      {!empty && (
        <div className="c-product-card__inline c-product-card__pricing">
          {priceBox}
          {renderIfProgress()}
        </div>
      )}
      {renderIfBody()}
      {renderIfStatus()}
      <div className="c-product-card__buttons">
        {buttons?.map((button, index) => {
          const cssClasses = classNames('c-product-card__buttons__item', button.props.className)
          const keyIdx = `card-button-${index}`
          return React.cloneElement(button, { className: cssClasses, key: keyIdx })
        })}
      </div>
      {renderIfExpandable()}
      {renderIfFootnotes()}
    </article>
  )
}

ProductCard.propTypes = {
  buttons: PropTypes.arrayOf(PropTypes.element), // Bronson template: '@bronson-button'. Use {@link Button} component.
  carousel: PropTypes.element, // Bronson template: '@bronson-carousel'. Use {@link Carousel} component.
  defaultExpanded: PropTypes.bool, // Bronson template: `[aria-expanded]` attribute on `.c-product-card__expandable` button.
  expandable: PropTypes.bool, // Bronson template: 'expandable'.
  expandableContent: PropTypes.element, // Bronson template: '@bronson-description-list--description-list-horizontal-with-short-terms'. Use {@link DescriptionList} component.
  expandableText: PropTypes.string, // Bronson template: 'expandable.text'.
  expandableTitle: PropTypes.string, // Bronson template: 'expandable.title'.
  expandableTriggerLabel: PropTypes.string, // Bronson template: 'expandable.trigger.label'.
  favoriteButton: PropTypes.bool, // Bronson template: '@bronson-favorite-button'.
  footnotes: PropTypes.element, // Bronson template: '@bronson-footnotes'. Use {@link Footnote} component.
  onClick: PropTypes.func, // Added for `[onClick]` attribute.
  priceBox: PropTypes.element, // Bronson template: '@bronson-price-box'. Use {@link PriceBox} component.
  productSubtitle: PropTypes.string, // Bronson template: 'product.subtitle'.
  productTitle: PropTypes.string, // Bronson template: 'product.title'.
  progressLabel: PropTypes.string, // Bronson template: 'progress.label'.
  status: PropTypes.node, // Bronson template: '@bronson-status-message'. Use {@link StatusMessage} component.
  tag: PropTypes.element, // Bronson template: '@bronson-tag'. Use {@link Tag} component.
  tagList: PropTypes.element, // Bronson template: '@bronson-tag-list'. Use {@link TagList} component.
  tagListTitle: PropTypes.string, // Bronson template: 'tag-list.title'.
  text: PropTypes.string, // Bronson template: 'body.text'.
  testId: PropTypes.string, // Added for the `[data-testid]` attribute.
  /**
   * Convenience props.
   */
  disabled: PropTypes.bool, // Convenience prop for `.c-product-card--inactive`. Bronson template: 'disabled'.
  empty: PropTypes.bool, // Convenience prop for `.c-product-card--empty`. Bronson template: 'empty'.
  /**
   * Deprecated props.
   */
  expandableDefinitionList: deprecated(
    PropTypes.element,
    'Deprecated, please use `expandableContent` instead. Prop will be removed in v6.0.0.'
  ),
}
